import { Link } from "wouter";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import logo from "@assets/graystone-logo.gif";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const navLinks = [
    {
      name: "Roofing",
      href: "/services/roofing-company-lancaster-pa/",
      dropdown: [
        { name: "Roof Repair", href: "/services/roof-repair-lancaster-pa/" },
        { name: "Roof Inspections", href: "/services/roofing-inspection-lancaster-pa/" },
        { name: "Free Estimates", href: "/services/free-roof-estimates-lancaster-pa/" },
        { name: "Metal Roofing", href: "/services/metal-roofing-lancaster-pa/" },
        { name: "Rubber Roofing", href: "/services/rubber-roofing-installation-lancaster-pa/" },
        { name: "Shingle Roofing", href: "/services/shingle-roofing-lancaster-pa/" },
        { name: "Storm Damage", href: "/services/roofing-storm-damage-lancaster-pa/" }
      ]
    },
    {
      name: "Siding",
      href: "/services/siding-contractor-lancaster-pa/"
    },
    {
      name: "Gutters",
      href: "/services/gutter-installation-lancaster-pa-york-pa/"
    },
    {
      name: "Service Areas",
      href: "/service-areas/"
    },
    {
      name: "Photos",
      href: "/roofing-siding-photos-lancaster-pa/"
    },
    {
      name: "Reviews",
      href: "/roofing-siding-york-lancaster-pa-testimonials/"
    },
    {
      name: "Help Hub",
      href: "/blog/"
    },
    {
      name: "About",
      href: "/about-graystone-roofing-siding-lancaster/"
    }
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-background border-b border-border shadow-sm">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <img src={logo} alt="Graystone Roofing & Siding" width={323} height={134} className="h-12 w-auto" data-testid="img-logo" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden xl:flex items-center gap-5" data-testid="nav-desktop">
          {navLinks.map((link) => (
            <div key={link.name} className="relative group">
              <Link href={link.href} className="text-foreground font-medium hover:text-primary transition-colors py-2 flex items-center gap-1 whitespace-nowrap">
                {link.name}
                {link.dropdown && <ChevronDown className="h-4 w-4" />}
              </Link>
              
              {link.dropdown && (
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-card border border-border shadow-lg rounded-md py-2 w-64">
                    {link.dropdown.map((item) => (
                      <Link 
                        key={item.name} 
                        href={item.href}
                        className="block px-4 py-2 text-sm text-card-foreground hover:bg-muted hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <a href="tel:+17177257170" className="flex items-center gap-2 text-foreground font-bold hover:text-primary transition-colors text-lg whitespace-nowrap" data-testid="link-phone-header">
            <Phone className="h-5 w-5 text-primary" />
            717-725-7170
          </a>
          <Link href="/contact-graystone-roofing-siding/" className="bg-primary text-primary-foreground px-5 py-2.5 rounded font-medium hover:opacity-90 transition-opacity whitespace-nowrap" data-testid="btn-header-cta">
            Request a Free Quote
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="xl:hidden p-2 text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="btn-mobile-menu"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileMenuOpen && (
        <div className="xl:hidden absolute top-20 left-0 w-full bg-background border-b border-border shadow-lg max-h-[calc(100vh-80px)] overflow-y-auto pb-20">
          <nav className="flex flex-col py-4 px-4 gap-2" data-testid="nav-mobile">
            <Link
              href="/"
              className="text-foreground font-medium py-3 text-lg"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="link-mobile-home"
            >
              Home
            </Link>
            {navLinks.map((link) => (
              <div key={link.name} className="flex flex-col">
                <div className="flex items-center justify-between">
                  <Link 
                    href={link.href} 
                    className="text-foreground font-medium py-3 flex-grow text-lg"
                    onClick={() => {
                      if (!link.dropdown) setMobileMenuOpen(false);
                    }}
                  >
                    {link.name}
                  </Link>
                  {link.dropdown && (
                    <button 
                      className="p-3"
                      onClick={() => toggleDropdown(link.name)}
                      aria-label={`Toggle ${link.name} submenu`}
                      aria-expanded={activeDropdown === link.name}
                    >
                      <ChevronDown className={`h-5 w-5 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                
                {link.dropdown && activeDropdown === link.name && (
                  <div className="flex flex-col pl-4 border-l-2 border-muted ml-2 pb-2">
                    {link.dropdown.map((item) => (
                      <Link 
                        key={item.name} 
                        href={item.href}
                        className="py-2 text-muted-foreground hover:text-primary transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <div className="mt-6 flex flex-col gap-4">
              <Link 
                href="/contact-graystone-roofing-siding/" 
                className="bg-primary text-primary-foreground px-5 py-3 rounded font-medium text-center hover:opacity-90 transition-opacity"
                onClick={() => setMobileMenuOpen(false)}
              >
                Request a Free Quote
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
